import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, Dumbbell, Check, Eye } from 'lucide-react';
import api from '@/services/apiClient';
import PageHeader from '@/components/ui/PageHeader';
import DataTable from '@/components/ui/DataTable';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import Input from '@/components/ui/Input';
import Toolbar from '@/components/ui/Toolbar';
import Modal from '@/components/ui/Modal';
import EmptyState from '@/components/ui/EmptyState';
import ExerciseForm from './ExerciseForm';
import ExerciseDetail from './ExerciseDetail';
import {
  MUSCLE_GROUPS, MUSCLE_LABELS, EQUIPMENT_LABELS, DIFFICULTY_LABELS, optsFrom,
} from '@/lib/enums';

/**
 * Catálogo de exercícios. `mode`:
 *  - 'global' (admin): cria/edita exercícios globais, scope=global
 *  - 'library' (trainer): vê todos (scope=all), cria os próprios
 */
export default function Exercises({ mode = 'global' }) {
  const { t } = useTranslation();
  const isAdmin = mode === 'global';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [muscleGroup, setMuscleGroup] = useState('');
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null); // {} novo | obj editar | null fechado
  const [viewing, setViewing] = useState(null); // detalhe (biblioteca do personal)

  const load = () => {
    setLoading(true);
    const params = { scope: isAdmin ? 'global' : 'all' };
    if (muscleGroup) params.muscleGroup = muscleGroup;
    if (q) params.q = q;
    api
      .get('/exercises', params)
      .then((res) => setRows((res.data || res).exercises || res.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const id = setTimeout(load, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [muscleGroup, q]);

  const save = async (form) => {
    if (editing?.id) await api.put(`/exercises/${editing.id}`, form);
    else await api.post('/exercises', form);
    setEditing(null);
    load();
  };

  const remove = async (id) => {
    if (!confirm(t('exercises.confirmDelete'))) return;
    try {
      await api.delete(`/exercises/${id}`);
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  const thumbStriped = {
    width: 44,
    height: 34,
    borderRadius: 6,
    flex: 'none',
    background:
      'repeating-linear-gradient(45deg, var(--surf2), var(--surf2) 5px, color-mix(in srgb, var(--surf2) 60%, var(--bg)) 5px, color-mix(in srgb, var(--surf2) 60%, var(--bg)) 10px)',
  };

  const columns = [
    {
      key: 'name',
      header: t('exercises.colExercise'),
      render: (r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {r.imageUrl ? (
            <img src={r.imageUrl} alt="" style={{ width: 44, height: 34, borderRadius: 6, objectFit: 'cover', flex: 'none' }} />
          ) : (
            <span style={thumbStriped} aria-hidden />
          )}
          <span
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
              fontSize: 15,
              textTransform: 'uppercase',
              color: 'var(--hi)',
            }}
          >
            {r.name}
          </span>
        </div>
      ),
    },
    { key: 'muscleGroup', header: t('exercises.colGroup'), render: (r) => <span style={{ color: 'var(--mid)', fontSize: 13 }}>{MUSCLE_LABELS[r.muscleGroup] || r.muscleGroup}</span> },
    { key: 'equipment', header: t('exercises.colEquipment'), render: (r) => <span style={{ color: 'var(--mid)', fontSize: 13 }}>{EQUIPMENT_LABELS[r.equipment] || r.equipment || '—'}</span> },
    {
      key: 'media',
      header: t('exercises.colMedia'),
      render: (r) =>
        r.videoUrl || r.imageUrl ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--volt)', fontSize: 13, fontWeight: 600 }}>
            {t('exercises.video')} <Check size={13} strokeWidth={2} />
          </span>
        ) : (
          <span style={{ color: 'var(--frost)', fontSize: 13 }}>{t('exercises.review')}</span>
        ),
    },
    { key: 'difficulty', header: t('exercises.colDifficulty'), render: (r) => <span style={{ color: 'var(--mid)', fontSize: 12 }}>{DIFFICULTY_LABELS[r.difficulty] || r.difficulty}</span> },
    {
      key: 'status',
      header: t('screens.status'),
      render: (r) => {
        const ok = r.videoUrl || r.imageUrl;
        return (
          <span
            style={{
              display: 'inline-block',
              width: 9,
              height: 9,
              borderRadius: '50%',
              background: ok ? 'var(--volt)' : 'var(--frost)',
            }}
            aria-label={ok ? 'ok' : t('exercises.review')}
          />
        );
      },
    },
    {
      key: 'actions',
      header: t('screens.actions'),
      align: 'right',
      render: (r) => {
        // trainer só edita os próprios (sem isGlobal); admin edita tudo.
        // Global na biblioteca: abre o detalhe (visualiza e sobe a própria mídia).
        const canEdit = isAdmin || !r.isGlobal;
        if (!canEdit) {
          return (
            <Button size="sm" variant="ghost" icon={Eye} onClick={() => setViewing(r)}>{t('exercises.view')}</Button>
          );
        }
        return (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button size="sm" variant="ghost" icon={Pencil} onClick={() => setEditing(r)}>{t('common.edit')}</Button>
            <Button size="sm" variant="ghost" icon={Trash2} onClick={() => remove(r.id)} />
          </div>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader kicker={isAdmin ? 'Movez · Admin' : 'Movez · Personal'} title={isAdmin ? t('nav.exercises') : t('nav.library')}>
        <Button icon={Plus} onClick={() => setEditing({})}>{t('screens.newExercise')}</Button>
      </PageHeader>

      <Toolbar>
        <Input placeholder={t('common.search')} value={q} onChange={(e) => setQ(e.target.value)} style={{ minWidth: 240 }} />
        <Select value={muscleGroup} onChange={(e) => setMuscleGroup(e.target.value)}
          options={[{ value: '', label: t('exercises.allGroups') }, ...optsFrom(MUSCLE_GROUPS, MUSCLE_LABELS)]} />
      </Toolbar>

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        empty={<EmptyState icon={Dumbbell} title={t('exercises.emptyTitle')} description={t('exercises.emptyDesc')} action={<Button icon={Plus} onClick={() => setEditing({})}>{t('screens.newExercise')}</Button>} />}
      />

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        kicker={editing?.id ? t('common.edit') : t('exercises.new')}
        title={t('exercises.modalTitle')}
        size="md"
      >
        {editing && <ExerciseForm initial={editing} onSubmit={save} onCancel={() => setEditing(null)} />}
      </Modal>

      <Modal
        open={!!viewing}
        onClose={() => setViewing(null)}
        kicker={t('exercises.global')}
        title={viewing?.name}
        size="md"
      >
        {viewing && (
          <ExerciseDetail exercise={viewing} onChanged={load} onClose={() => setViewing(null)} />
        )}
      </Modal>
    </>
  );
}
